/**
 * ClassificationBadge Component
 * Displays classification badges for familiarisation items
 *
 * Classifications:
 * - MANDATORY (red): Required by WBC3 for all vessels
 * - VESSEL_SPECIFIC (blue): Required for specific vessel types/equipment
 * - RECOMMENDED (yellow): Best practice beyond WBC3 minimums
 */

import React from 'react';
import { Shield, Ship, Lightbulb } from 'lucide-react';

// Badge configuration for each classification type
const CLASSIFICATION_CONFIG = {
  MANDATORY: {
    icon: Shield,
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
    label: 'Mandatory',
    tooltip: 'Required by WBC3 for all vessels'
  },
  VESSEL_SPECIFIC: {
    icon: Ship,
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Vessel-Specific',
    tooltip: 'Required for specific vessel types or equipment'
  },
  RECOMMENDED: {
    icon: Lightbulb,
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    label: 'Recommended',
    tooltip: 'Best practice beyond WBC3 minimums'
  }
};

/**
 * ClassificationBadge - Simple badge showing item classification
 *
 * @param {string} classification - MANDATORY, VESSEL_SPECIFIC, or RECOMMENDED
 * @param {boolean} compact - If true, shows abbreviated version
 * @param {boolean} showIcon - If true, shows icon (default true)
 */
const ClassificationBadge = ({
  classification,
  compact = false,
  showIcon = true
}) => {
  // Get config for this classification (default to MANDATORY if unknown)
  const config = CLASSIFICATION_CONFIG[classification] || CLASSIFICATION_CONFIG.MANDATORY;
  const { icon: Icon, bg, text, border, label, tooltip } = config;

  // Compact version - just colored dot
  if (compact) {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${bg.replace('100', '500')}`}
        title={tooltip}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${bg} ${text} ${border}`}
      title={tooltip}
    >
      {showIcon && <Icon className="w-3 h-3" />}
      <span>{label}</span>
    </span>
  );
};

/**
 * ClassificationLegend - Shows all classification types with descriptions
 * Useful for displaying at the top of familiarisation checklists
 */
export const ClassificationLegend = ({ className = '' }) => {
  return (
    <div className={`flex flex-wrap gap-3 text-xs ${className}`}>
      {Object.entries(CLASSIFICATION_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        return (
          <div
            key={key}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${config.bg} ${config.text} ${config.border}`}
            title={config.tooltip}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="font-medium">{config.label}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * getClassificationStats - Helper to calculate classification distribution
 * @param {Array} items - Array of familiarisation items with classification field
 * @returns {Object} Counts for each classification type
 */
export const getClassificationStats = (items = []) => {
  const stats = {
    MANDATORY: 0,
    VESSEL_SPECIFIC: 0,
    RECOMMENDED: 0,
    total: items.length
  };

  items.forEach(item => {
    const classification = item.classification || 'MANDATORY';
    if (stats[classification] !== undefined) {
      stats[classification]++;
    }
  });

  return stats;
};

export default ClassificationBadge;
